import type { EndpointBuilder } from "@reduxjs/toolkit/query/react";
import { createApi } from "@reduxjs/toolkit/query/react";

import { TAGS } from "./common/tags";

import { magentoBaseQuery } from "./baseQuery";
import {
  addPromoGiftToCartEndpoint,
  applyCouponsToCartEndpoint,
  getAvailablePaymentMethodsEndpoint,
  getCartItemsEndpoint,
  getCountryEndpoint,
  getCustomerCartEndpoint,
  getCustomerCartIdEndpoint,
  getCustomerCartOrderPricesEndpoint,
  getCustomerCouponsEndpoint,
  getCustomerOrderAllStore,
  getCustomerOrderEndpoint,
  getCustomerOrdersEndpoint,
  getCustomerOrdersStatusesEndpoint,
  getLastCustomerOrderEndpoint,
  getOrderAttributesEndpoint,
  getOrderDeliveryLimitsEndpoint,
  getPromoGiftInfoEndpoint,
  getShippingMethodsEndpoint,
  getStoreConfigEndpoint,
  placeOrderEndpoint,
  removeCouponsFromCartEndpoint,
  removeItemFromCartEndpoint,
  setAdditionalAttributesEndpoint,
  setBillingAddressOnCartEndpoint,
  setCheckoutFastFlowCartEndpoint,
  setPaymentMethodOnCartEndpoint,
  setShippingAddressOnCartEndpoint,
  setShippingAddressOnCartWithMethodsEndpoint,
  setShippingMethodsOnCartEndpoint,
  updateOrderPaymentMethodEndpoint,
} from "./endpoints";

export type MagentoApiBuilder = EndpointBuilder<
  typeof magentoBaseQuery,
  | typeof TAGS.CART_ID
  | typeof TAGS.CART_ITEMS
  | typeof TAGS.CART_ORDER_PRICES
  | typeof TAGS.COUPONS
  | typeof TAGS.COUNTRY
  | typeof TAGS.STORE_CONFIG
  | typeof TAGS.ORDER
  | typeof TAGS.ORDER_DELIVERY_LIMITS
  | typeof TAGS.ORDER_ATTRIBUTES
  | typeof TAGS.AVAILABLE_PAYMENT_METHODS
  | typeof TAGS.SHIPPING_METHODS,
  "magentoApi"
>;

export const magentoApiSlice = createApi({
  reducerPath: "magentoApi",
  baseQuery: magentoBaseQuery,
  tagTypes: [
    TAGS.CART_ID,
    TAGS.CART_ITEMS,
    TAGS.CART_ORDER_PRICES,
    TAGS.COUPONS,
    TAGS.COUNTRY,
    TAGS.STORE_CONFIG,
    TAGS.ORDER,
    TAGS.ORDER_DELIVERY_LIMITS,
    TAGS.ORDER_ATTRIBUTES,
    TAGS.AVAILABLE_PAYMENT_METHODS,
    TAGS.SHIPPING_METHODS,
  ],
  endpoints: (builder) => ({
    ...getCustomerCartEndpoint(builder),
    ...getCustomerCartIdEndpoint(builder),
    ...getCartItemsEndpoint(builder),
    ...getCustomerCouponsEndpoint(builder),
    ...getCustomerOrderEndpoint(builder),
    ...getCustomerOrdersStatusesEndpoint(builder),
    ...getCustomerOrdersEndpoint(builder),
    ...getCustomerCartOrderPricesEndpoint(builder),
    ...getCustomerOrderEndpoint(builder),
    ...getOrderAttributesEndpoint(builder),
    ...getCountryEndpoint(builder),
    ...getStoreConfigEndpoint(builder),
    ...getOrderDeliveryLimitsEndpoint(builder),
    ...getAvailablePaymentMethodsEndpoint(builder),
    ...setShippingAddressOnCartEndpoint(builder),
    ...setShippingAddressOnCartWithMethodsEndpoint(builder),
    ...applyCouponsToCartEndpoint(builder),
    ...removeCouponsFromCartEndpoint(builder),
    ...getShippingMethodsEndpoint(builder),
    ...setShippingMethodsOnCartEndpoint(builder),
    ...setBillingAddressOnCartEndpoint(builder),
    ...setCheckoutFastFlowCartEndpoint(builder),
    ...placeOrderEndpoint(builder),
    ...setAdditionalAttributesEndpoint(builder),
    ...setPaymentMethodOnCartEndpoint(builder),
    ...getLastCustomerOrderEndpoint(builder),
    ...updateOrderPaymentMethodEndpoint(builder),
    ...removeItemFromCartEndpoint(builder),
    ...addPromoGiftToCartEndpoint(builder),
    ...getPromoGiftInfoEndpoint(builder),
    ...getCustomerOrderAllStore(builder),
  }),
});

export const {
  useGetCustomerCartQuery,
  useGetCustomerCartIdQuery,
  useGetCartItemsQuery,
  useGetCustomerCouponsQuery,
  useGetCustomerOrdersStatusesQuery,
  useGetCustomerCartOrderPricesQuery,
  useGetCustomerOrderQuery,
  useGetCustomerOrdersQuery,
  useGetOrderAttributesQuery,
  useGetCountryQuery,
  useGetStoreConfigQuery,
  useGetOrderDeliveryLimitsQuery,
  useApplyCouponsToCartMutation,
  useRemoveCouponsFromCartMutation,
  useSetShippingAddressOnCartMutation,
  useSetShippingAddressOnCartWithMethodsMutation,
  useGetAvailablePaymentMethodsQuery,
  useGetShippingMethodsQuery,
  useSetShippingMethodsOnCartMutation,
  useSetBillingAddressOnCartMutation,
  useSetCheckoutFastFlowCartMutation,
  usePlaceOrderMutation,
  useSetAdditionalAttributesMutation,
  useSetPaymentMethodOnCartMutation,
  useGetLastCustomerOrderQuery,
  useUpdateOrderPaymentMethodMutation,
  useRemoveItemFromCartMutation,
  useAddPromoGiftToCartMutation,
  useGetPromoGiftInfoQuery,
  useGetCustomerOrdersAllStoresQuery,
} = magentoApiSlice;
